import React, { useCallback } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { useNavigate } from 'react-router'

import { isToday, isYesterday } from 'date-fns'
import styled, { useTheme } from 'styled-components/native'

import {
  ProviderSelfHarmTaskArguments,
  ProviderTaskType,
  ProviderTriageRiskTaskArguments,
} from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { TriageRiskLevel } from '@lyrahealth-inc/shared-app-logic/src/models/searchForCare/SearchForCare'
import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { AlertLoudIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/AlertLoudIcon'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import CollapsiblePanel from './CollapsiblePanel'
import { CLIENT_ACTIVITIES } from '../../../common/constants/routingConstants'
import { useAppDispatch } from '../../../data/store'
import { setSelectedClientId } from '../../../lyraTherapy/clients/clientDetails/data/ltClientSlice'
import { formatRiskValue, getRiskDetailRows, isRiskPositive, RiskDetailRow } from '../utils'

const DetailsSection = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.backgroundElement,
  padding: theme.spacing['16px'],
  gap: theme.spacing['8px'],
}))

const DetailRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['8px'],
}))

const DetailCell = styled.View({
  flex: 1,
})

const SourceRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
})

const SourceLabel = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
}))

const formatEventDate = (date: Date, intl: IntlShape): string => {
  const time = intl.formatDate(date, { hour: 'numeric', minute: 'numeric' })
  if (isToday(date)) {
    return intl.formatMessage({ defaultMessage: 'Today, {time}', description: 'Today with time' }, { time })
  }
  if (isYesterday(date)) {
    return intl.formatMessage({ defaultMessage: 'Yesterday, {time}', description: 'Yesterday with time' }, { time })
  }
  const fullDate = intl.formatDate(date, { month: 'numeric', day: 'numeric', year: 'numeric' })
  return intl.formatMessage(
    { defaultMessage: '{date}, {time}', description: 'Full date with time' },
    { date: fullDate, time },
  )
}

type DetailedAlertViewProps = {
  taskArguments?: ProviderTriageRiskTaskArguments | ProviderSelfHarmTaskArguments
  taskType: ProviderTaskType
  clientId?: string
  eventDate?: string | null
  isExpanded: boolean
  onToggleExpanded: () => void
}

const getRiskLevelTitle = (riskLevel?: string | null): React.ReactNode => {
  switch (riskLevel) {
    case TriageRiskLevel.RED:
      return <FormattedMessage defaultMessage='High risk' description='High risk label for detailed alert view' />
    case TriageRiskLevel.AMBER:
      return (
        <FormattedMessage defaultMessage='Moderate risk' description='Moderate risk label for detailed alert view' />
      )
    case TriageRiskLevel.YELLOW:
      return (
        <FormattedMessage
          defaultMessage='Low imminent risk'
          description='Low imminent risk label for detailed alert view'
        />
      )
    default:
      return <FormattedMessage defaultMessage='At risk' description='Risk label for detailed alert view' />
  }
}

const isHighRisk = (riskLevel?: string | null): boolean => riskLevel === TriageRiskLevel.RED

const DetailedAlertView: React.FC<DetailedAlertViewProps> = ({
  taskArguments,
  taskType,
  clientId,
  eventDate,
  isExpanded,
  onToggleExpanded,
}) => {
  const { colors } = useTheme()
  const intl = useIntl()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isSelfHarm = taskType === ProviderTaskType.SELF_HARM

  const handleViewAssessment = useCallback(() => {
    if (clientId) {
      dispatch(setSelectedClientId(clientId))
    }
    navigate(CLIENT_ACTIVITIES.route)
  }, [clientId, dispatch, navigate])

  const detailRows: RiskDetailRow[] =
    taskType === ProviderTaskType.TRIAGE_RISK ? getRiskDetailRows(taskArguments as ProviderTriageRiskTaskArguments) : []

  const sourceLabel = isSelfHarm ? (
    <FormattedMessage defaultMessage='Assessment' description='Risk data source assessment' />
  ) : (
    <FormattedMessage defaultMessage='Search for care' description='Risk data source for DA provider' />
  )

  const triageRiskLevel = taskArguments && 'risk_level' in taskArguments ? taskArguments.risk_level : undefined
  const useErrorStyling = isSelfHarm || isHighRisk(triageRiskLevel)

  return (
    <CollapsiblePanel
      isExpanded={isExpanded}
      onToggleExpanded={onToggleExpanded}
      icon={<AlertLoudIcon fillColor={useErrorStyling ? colors.iconError : colors.iconWarning} />}
      title={isSelfHarm ? getRiskLevelTitle() : getRiskLevelTitle(triageRiskLevel)}
      subtitle={
        <FormattedMessage defaultMessage='Review details below.' description='Risk level subtitle for detailed view' />
      }
      textColor={useErrorStyling ? colors.textError : colors.textWarning}
      backgroundColor={useErrorStyling ? colors.backgroundError : colors.backgroundWarning}
      testIDPrefix='RiskAlertPopover'
      accessibilityLabel={intl.formatMessage(
        {
          defaultMessage: '{state} risk details',
          description: 'Accessibility label for risk alert details toggle',
        },
        { state: isExpanded ? 'Collapse' : 'Expand' },
      )}
    >
      <DetailsSection>
        {detailRows
          .filter((row) => row.value != null)
          .map((row) => (
            <DetailRow key={row.key} testID={tID(`DetailedAlertView-row-${row.key}`)}>
              <DetailCell>
                <BodyText size={BodyTextSize.SMALL} color={colors.textPrimary} text={row.label} />
              </DetailCell>
              <DetailCell>
                <BodyText
                  size={BodyTextSize.SMALL}
                  bold
                  text={formatRiskValue(row.value, intl)}
                  color={row.alwaysError || isRiskPositive(row.value) ? colors.textError : colors.textPrimary}
                />
              </DetailCell>
            </DetailRow>
          ))}
        <SourceRow>
          <SourceLabel>
            <BodyText
              size={BodyTextSize.CAPTION}
              color={colors.textSecondary}
              text={<FormattedMessage defaultMessage='Source:' description='Risk data source label' />}
            />
            {isSelfHarm && clientId ? (
              <Link
                text={sourceLabel}
                onPress={handleViewAssessment}
                testID={tID('DetailedAlertView-source-link')}
                size={BodyTextSize.CAPTION}
                accessibilityLabel={intl.formatMessage({
                  defaultMessage: 'View assessment',
                  description: 'Accessibility label for source assessment link',
                })}
              />
            ) : (
              <BodyText size={BodyTextSize.CAPTION} color={colors.textSecondary} text={sourceLabel} />
            )}
          </SourceLabel>
          {eventDate && (
            <BodyText
              size={BodyTextSize.CAPTION}
              color={colors.textSecondary}
              text={formatEventDate(new Date(eventDate), intl)}
              testID={tID('DetailedAlertView-event-date')}
            />
          )}
        </SourceRow>
      </DetailsSection>
    </CollapsiblePanel>
  )
}

export default DetailedAlertView
